import {
  CSSProperties,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  createContext,
} from 'react';
import { ChevronRight, Layers, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { useReactTable, getCoreRowModel, flexRender, type Row } from '@tanstack/react-table';
import { SpinnerOverlay } from '@/components/ui/Spinner';

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  type DragEndEvent,
  type UniqueIdentifier,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DraggableAttributes } from '@dnd-kit/core';

import type { Layer } from './types';
import { useColumns, setDragContext } from './Columns';
import { cn } from '../../utils/cn';
import CompositionLayerDialog from './CompositionLayerDialog';
import { useLayerOperations } from './LayerOperationsContext';

// Context for passing drag handlers to cells
type DragContextType = {
  attributes?: DraggableAttributes;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  listeners?: Record<string, Function>;
};

const DragCtx = createContext<DragContextType>({});

// Export context to Columns
setDragContext(DragCtx);

// Row Component
const DraggableRow = ({ row, index }: { row: Row<Layer>; index: number }) => {
  const { transform, transition, setNodeRef, isDragging, attributes, listeners } = useSortable({
    id: row.original.id,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  };

  return (
    <DragCtx.Provider value={{ attributes, listeners }}>
      <tr
        ref={setNodeRef}
        style={style}
        className={cn(
          'group/row border-b border-neutral-100 last:border-b-0 hover:bg-neutral-25 transition-colors'
        )}
      >
        {row.getVisibleCells().map((cell) => (
          <td
            key={cell.id}
            className="px-5 py-3.5 text-sm text-neutral-800 leading-relaxed"
            style={{ width: cell.column.getSize() }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    </DragCtx.Provider>
  );
};

const CompositionLayersTable = () => {
  const { entityId, layers, isLoading, error, updateLayerAsync, onSuccess } = useLayerOperations();

  // Local state for optimistic updates during drag & drop
  const [localLayers, setLocalLayers] = useState<Layer[]>([]);
  const layersRef = useRef<Layer[]>([]);

  // Update local layers when data from API changes
  useEffect(() => {
    if (layers?.length > 0) {
      setLocalLayers(layers);
      layersRef.current = layers;
    }
  }, [layers]);

  const displayLayers = localLayers.length > 0 ? localLayers : layers;

  const columns = useColumns();

  const table = useReactTable({
    data: displayLayers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const dataIds = useMemo<UniqueIdentifier[]>(
    () => table.getRowModel().rows.map((row) => row.original.id),
    [table.getRowModel().rows]
  );

  // reorder rows after drag & drop
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        setLocalLayers((currentData) => {
          const dataToUse = currentData.length > 0 ? currentData : layers;

          const oldIndex = dataToUse.findIndex((layer) => layer.id === active.id);
          const newIndex = dataToUse.findIndex((layer) => layer.id === over.id);

          if (oldIndex === -1 || newIndex === -1) {
            return currentData;
          }

          const originalData = [...dataToUse];
          const reorderedLayers = arrayMove(dataToUse, oldIndex, newIndex);
          layersRef.current = reorderedLayers;

          const updatePromises = reorderedLayers.map((layer, index) => {
            const newOrder = index + 1;
            return updateLayerAsync({
              instanceTypeId: entityId,
              layerId: layer.id,
              data: { order: newOrder },
            });
          });

          Promise.all(updatePromises)
            .then(() => {
              onSuccess();
            })
            .catch((error) => {
              console.error('Error updating layer order:', error);
              setLocalLayers(originalData);
              layersRef.current = originalData;
            });

          return reorderedLayers;
        });
      }
    },
    [entityId, updateLayerAsync, layers, onSuccess]
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  if (error) {
    return (
      <div className="mb-1">
        <div className="p-4 text-red-500">
          Chyba při načítání vrstev: {error instanceof Error ? error.message : 'Neznámá chyba'}
        </div>
      </div>
    );
  }

  const [open, setOpen] = useState(true);
  const count = displayLayers.length;

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between py-3">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 hover:text-primary-700"
        >
          <ChevronRight
            size={14}
            className={cn('text-neutral-400 transition-transform', open && 'rotate-90')}
          />
          <span className="text-sm font-semibold tracking-tight">Vrstvy skladby</span>
          <Badge variant="outline" size="sm">
            {count}
          </Badge>
        </button>
        <div className="flex items-center gap-1.5">
          <CompositionLayerDialog
            key="add-layer"
            layer={null}
            variant="add"
            layers={displayLayers || layers}
          />
        </div>
      </div>
      {open && (
        <div className="border border-neutral-150 rounded overflow-hidden bg-white mb-1">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <SpinnerOverlay size="lg" tip="Načítání vrstev..." />
            </div>
          ) : count > 0 ? (
            <DndContext
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            >
              <table className="w-full">
                <thead className="border-b border-neutral-200 bg-neutral-25">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          className="px-3 py-2 text-left text-[11px] uppercase tracking-wider font-medium text-neutral-500 border-r border-neutral-150 last:border-r-0"
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  <SortableContext items={dataIds} strategy={verticalListSortingStrategy}>
                    {table.getRowModel().rows.map((row, index) => (
                      <DraggableRow key={row.original.id} row={row} index={index} />
                    ))}
                  </SortableContext>
                </tbody>
              </table>
            </DndContext>
          ) : (
            <EmptyState
              icon={<Layers size={20} />}
              title="Bez definovaných vrstev"
              description="Přidejte první vrstvu skladby"
              actionLabel="Přidat vrstvu"
              onAction={() => {
                // Trigger the CompositionLayerDialog add button
                const btn = document.querySelector(
                  '[data-testid="add-layer-btn"]'
                ) as HTMLButtonElement;
                btn?.click();
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default CompositionLayersTable;
